import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  getAgencyRequests,
  getAgencyBookingHistory,
  getBookingRequestById,
  getAgencyUsers,
  getAgencyBookingRequests,
  respondToBookingRequest
} from "../services/api";
import AgentBookingModal from "../components/AgentBookingModal";
import "./AgentDashboard.css";

// Direct API access for critical operations
const directApi = axios.create({
  baseURL: `${window.location.origin}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

const AgentDashboard = () => {
  const { currentUser, isAgentAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [agency, setAgency] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [safeMode, setSafeMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [tabsVisible, setTabsVisible] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tabLastRefreshTimes, setTabLastRefreshTimes] = useState({
    overview: null,
    users: null,
    bookings: null,
    history: null,
    requests: null,
    property: null
  });
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBookingForModal, setSelectedBookingForModal] = useState(null);
  const [bookingDocuments, setBookingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  
  // Helper function to ensure document URLs are correctly formed
  const getAbsoluteDocumentUrl = (documentUrl) => {
    if (!documentUrl) return '';
    
    console.log("Processing document URL:", documentUrl);
    
    // If already absolute URL with http/https, return as is
    if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
      // If the URL has localhost, but we're not on localhost, update it
      if (documentUrl.includes('localhost') && window.location.hostname !== 'localhost') {
        const currentOrigin = window.location.origin;
        // Extract the path portion (after the domain)
        const urlParts = documentUrl.split('/');
        // Find the uploads part in the URL
        const uploadsIndex = urlParts.findIndex(part => part === 'uploads');
        
        if (uploadsIndex >= 0) {
          const newUrl = `${currentOrigin}/${urlParts.slice(uploadsIndex).join('/')}`;
          console.log("Converted localhost URL to:", newUrl);
          return newUrl;
        }
      }
      return documentUrl;
    }
    
    // If it's a relative URL starting with /
    if (documentUrl.startsWith('/')) {
      const absoluteUrl = `${window.location.origin}${documentUrl}`;
      console.log("Converted relative URL to absolute:", absoluteUrl);
      return absoluteUrl;
    }
    
    // If it's a relative URL without leading slash
    const absoluteUrl = `${window.location.origin}/${documentUrl}`;
    console.log("Added origin to URL:", absoluteUrl);
    return absoluteUrl;
  };

  // Function to update the refresh time for a specific tab
  const updateTabRefreshTime = (tab) => {
    const now = new Date();
    const formattedTime = now.toLocaleTimeString();
    
    setTabLastRefreshTimes(prev => ({
      ...prev,
      [tab]: formattedTime
    }));
    
    setLastRefresh(formattedTime);
  };

  // Function to fetch documents for a booking
  const fetchBookingDocuments = async (bookingId) => {
    try {
      setLoadingDocuments(true);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      // Try to get documents from the dedicated API endpoint
      try {
        const docsResponse = await axios.get(`/api/uploads/documents/${bookingId}`, config);
        
        if (docsResponse.data && Array.isArray(docsResponse.data) && docsResponse.data.length > 0) {
          console.log('Successfully loaded documents from API:', docsResponse.data);
          setBookingDocuments(docsResponse.data);
          return;
        } else {
          console.log('No documents found from API endpoint for booking:', bookingId);
        }
      } catch (apiErr) {
        console.error('Error loading documents from API:', apiErr);
      }
      
      // Fallback: Get the booking details and extract document info manually
      const response = await axios.get(`/api/bookings/${bookingId}`, config);
      const booking = response.data;
      
      console.log('Checking for documents in booking object:', booking);
      
      // Collect all document types from the booking
      const documents = [];
      const baseUrl = window.location.origin;
      
      // Helper function to format URL
      const formatUrl = (url) => {
        if (!url) return null;
        return url.startsWith('http') 
          ? url 
          : `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      };
      
      // Check for standard document
      if (booking.documentUrl) {
        console.log('Standard document found:', booking.documentUrl);
        documents.push({
          documentUrl: formatUrl(booking.documentUrl),
          documentType: 'uploaded_document',
          documentName: booking.documentOriginalName || 'Uploaded Document'
        });
      }
      
      // Check for cheque document
      if (booking.chequeUrl) {
        console.log('Cheque document found:', booking.chequeUrl);
        documents.push({
          documentUrl: formatUrl(booking.chequeUrl),
          documentType: 'cheque',
          documentName: booking.chequeOriginalName || 'Payment Cheque'
        });
      }
      
      // Check for IC front document
      if (booking.icFrontUrl) {
        console.log('IC Front document found:', booking.icFrontUrl);
        documents.push({
          documentUrl: formatUrl(booking.icFrontUrl),
          documentType: 'ic_front',
          documentName: booking.icFrontOriginalName || 'IC Front'
        });
      }
      
      // Check for IC back document
      if (booking.icBackUrl) {
        console.log('IC Back document found:', booking.icBackUrl);
        documents.push({
          documentUrl: formatUrl(booking.icBackUrl),
          documentType: 'ic_back',
          documentName: booking.icBackOriginalName || 'IC Back'
        });
      }
      
      console.log('Final document list:', documents);
      setBookingDocuments(documents);
    } catch (err) {
      console.error('Error loading booking documents:', err);
      setBookingDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Function to download a document
  const downloadDocument = (documentUrl, documentName) => {
    if (!documentUrl) {
      console.error('No document URL provided');
      return;
    }
    
    const formattedUrl = getAbsoluteDocumentUrl(documentUrl);
    console.log('Downloading document from URL:', formattedUrl);
    
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = formattedUrl;
    link.download = documentName || 'document';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to repair missing agency requests by checking localStorage
  const repairAgencyRequests = () => {
    try {
      if (!currentUser || !currentUser.agencyId) {
        console.warn("Cannot repair: No current user or agency ID");
        return;
      }

      // Get existing requests
      const storedRequestsJson = localStorage.getItem("agencyRequests");
      const storedRequests = storedRequestsJson
        ? JSON.parse(storedRequestsJson)
        : [];

      if (!Array.isArray(storedRequests)) {
        console.error("Invalid agencyRequests format in localStorage");
        localStorage.setItem("agencyRequests", JSON.stringify([]));
        return;
      }

      console.log(
        `Found ${storedRequests.length} existing agency requests in localStorage`
      );

      // See if we can find any users with pending agency requests
      const repairAttempts = [];

      // Check if there are any other users in localStorage
      try {
        // Check each storage location
        ["users", "userCache", "recentUsers"].forEach((storageKey) => {
          const dataJson = localStorage.getItem(storageKey);
          if (!dataJson) return;

          try {
            const data = JSON.parse(dataJson);

            // Handle array format
            if (Array.isArray(data)) {
              data.forEach((user) => {
                if (
                  user &&
                  user._id &&
                  user.agencyId === currentUser.agencyId &&
                  user.agencyStatus === "pending"
                ) {
                  repairAttempts.push({
                    _id: user._id,
                    userId: user._id,
                    name: user.name || "Unknown User",
                    email: user.email || "No Email",
                    phone: user.phone || "N/A",
                    agencyId: user.agencyId,
                    agencyRequestDate:
                      user.agencyRequestDate || new Date().toISOString(),
                    agencyRequestMessage:
                      user.agencyRequestMessage || "No message provided",
                    status: "pending",
                  });
                }
              });
            }
            // Handle object format
            else if (typeof data === "object" && data !== null) {
              // If it's a map of user ID to user
              Object.values(data).forEach((user) => {
                if (
                  user &&
                  user._id &&
                  user.agencyId === currentUser.agencyId &&
                  user.agencyStatus === "pending"
                ) {
                  repairAttempts.push({
                    _id: user._id,
                    userId: user._id,
                    name: user.name || "Unknown User",
                    email: user.email || "No Email",
                    phone: user.phone || "N/A",
                    agencyId: user.agencyId,
                    agencyRequestDate:
                      user.agencyRequestDate || new Date().toISOString(),
                    agencyRequestMessage:
                      user.agencyRequestMessage || "No message provided",
                    status: "pending",
                  });
                }
              });
            }
          } catch (parseError) {
            console.warn(`Error parsing ${storageKey}:`, parseError);
          }
        });
      } catch (e) {
        console.error("Error searching user data for repair:", e);
      }

      console.log(
        `Found ${repairAttempts.length} users with pending agency requests`
      );

      // Now add any requests that don't already exist
      let addedCount = 0;
      const updatedRequests = [...storedRequests];

      repairAttempts.forEach((newRequest) => {
        // Check if this request already exists
        const exists = storedRequests.some(
          (req) => req._id === newRequest._id || req.userId === newRequest._id
        );

        if (!exists) {
          updatedRequests.push(newRequest);
          addedCount++;
        }
      });

      // Save if we added any new requests
      if (addedCount > 0) {
        localStorage.setItem("agencyRequests", JSON.stringify(updatedRequests));
        console.log(
          `Added ${addedCount} missing agency requests to localStorage`
        );

        // Update state to reflect the changes
        setAgencyRequests(
          updatedRequests.filter(
            (req) =>
              req.agencyId === currentUser.agencyId && req.status === "pending"
          )
        );

        return { added: addedCount, total: updatedRequests.length };
      } else {
        console.log("No new agency requests to add");
        return { added: 0, total: updatedRequests.length };
      }
    } catch (error) {
      console.error("Error repairing agency requests:", error);
      return { error: error.message };
    }
  };

  const fetchAgencyRequests = async () => {
    try {
      console.log("Fetching agency membership requests...");
      
      // Reset error state
      setError("");
      
      // Make sure we have the current user's agency ID
      if (!currentUser?.agencyId) {
        console.error("Cannot fetch agency requests: No agency ID available");
        return;
      }
      
      let requestsData = [];
      
      // First try to get requests from the API
      try {
        console.log(`Fetching agency requests for agency ID: ${currentUser.agencyId}`);
        const requests = await getAgencyRequests(currentUser.agencyId);
        console.log("Agency membership requests data received:", requests);
        
        if (Array.isArray(requests) && requests.length > 0) {
          requestsData = requests;
          
          // Store in localStorage as fallback for future
          try {
            localStorage.setItem('agencyRequests', JSON.stringify(requests));
            console.log("Updated localStorage with API agency requests");
          } catch (storageErr) {
            console.warn("Could not update localStorage:", storageErr);
          }
        } else {
          console.log("No agency requests found from API or invalid format");
        }
      } catch (apiErr) {
        console.warn("Failed to fetch agency requests from API:", apiErr);
      }
      
      // Check localStorage as fallback or supplementary source
      try {
        console.log("Checking localStorage for agency membership requests");
        const localRequestsJson = localStorage.getItem('agencyRequests');
        
        if (localRequestsJson) {
          const localRequests = JSON.parse(localRequestsJson);
          
          if (Array.isArray(localRequests)) {
            // Filter for requests related to this agency and with pending status
            const relevantRequests = localRequests.filter(req => 
              req.agencyId === currentUser.agencyId && 
              req.status === 'pending'
            );
            
            console.log(`Found ${relevantRequests.length} agency requests in localStorage`);
            
            // Merge with API results, avoiding duplicates
            if (relevantRequests.length > 0) {
              // Add any local requests that aren't already in the API results
              relevantRequests.forEach(localReq => {
                const isDuplicate = requestsData.some(apiReq => 
                  (apiReq._id === localReq._id) || 
                  (apiReq.userId === localReq.userId && apiReq.agencyId === localReq.agencyId)
                );
                
                if (!isDuplicate) {
                  requestsData.push(localReq);
                }
              });
            }
          }
        }
      } catch (localErr) {
        console.error("Error reading agency requests from localStorage:", localErr);
      }
      
      // Try repair as a last resort if no requests found
      if (requestsData.length === 0) {
        console.log("No agency requests found, attempting repair...");
        const repairResult = repairAgencyRequests();
        console.log("Repair result:", repairResult);
      }
      
      // Final update to state
      console.log(`Setting state with ${requestsData.length} agency requests`);
      setAgencyRequests(requestsData);
    } catch (error) {
      console.error("Error fetching agency requests:", error);
      setError("Failed to load agency membership requests");
    }
  };

  // Debug function to log objects safely
  const safeLogObject = (obj) => {
    try {
      // Create a safe copy with circular references handled
      const getCircularReplacer = () => {
        const seen = new WeakSet();
        return (key, value) => {
          if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
              return "[Circular]";
            }
            seen.add(value);
          }
          return value;
        };
      };
      
      // Convert to string and back to remove functions and handle circular refs
      return JSON.parse(JSON.stringify(obj, getCircularReplacer()));
    } catch (err) {
      console.warn("Failed to safely log object:", err);
      return { error: "Object couldn't be safely logged" };
    }
  };

  // Function to fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      const fetchTasks = [];
      
      // Add various fetch tasks according to active tab and user role
      if (activeTab === "overview" || tabsVisible) {
        if (isAgentAdmin()) {
          fetchTasks.push(
            getAgencyBookingHistory()
              .then((data) => {
                // Filter to exclude user requests (pending_agent status)
                const filteredHistory = data.filter(booking => booking.status !== 'pending_agent');
                setBookingHistory(filteredHistory);
                console.log("Booking history loaded", filteredHistory.length, "items");
              })
              .catch((error) => {
                console.error("Error fetching booking history:", error);
                setBookingHistory([]);
              })
          );
        }
      }
      
      if (activeTab === "bookings" || tabsVisible) {
        try {
          console.log("Fetching booking requests...");
          // Use the getAgencyBookingRequests API function to get only user requests
          const bookingRequestsData = await getAgencyBookingRequests();
          setBookingRequests(bookingRequestsData);
          console.log("Booking requests loaded:", bookingRequestsData.length, "items");
          updateTabRefreshTime('bookings');
        } catch (err) {
          console.error("Error fetching booking requests:", err);
          setBookingRequests([]);
        }
      }

      // Fetch agent's agency
      if (currentUser?.agencyId) {
        try {
          const agencyRes = await axios.get(
            `/api/agencies/${currentUser.agencyId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          setAgency(agencyRes.data);
          console.log("Agency data fetched:", safeLogObject(agencyRes.data));
        } catch (agencyErr) {
          console.warn("Failed to fetch agency details:", agencyErr);
        }
      }

      // Fetch agency users - try multiple endpoints for better robustness
      let agencyUsers = [];
      let fetchSuccess = false;
      
      // Try the direct agency users endpoint first
      try {
        console.log(`Fetching users for agency: ${currentUser.agencyId}`);
        const usersRes = await axios.get(`/api/agencies/${currentUser.agencyId}/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (Array.isArray(usersRes.data)) {
          agencyUsers = usersRes.data;
          fetchSuccess = true;
          console.log(`Found ${agencyUsers.length} users from agency users endpoint`);
        }
      } catch (usersErr) {
        console.warn("Failed to fetch agency users from primary endpoint:", usersErr);
      }
      
      // If the first attempt failed, try an alternative endpoint
      if (!fetchSuccess || agencyUsers.length === 0) {
        try {
          console.log("Trying alternative endpoint for agency users");
          const altUsersRes = await axios.get(`/api/users/agency/${currentUser.agencyId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          
          if (Array.isArray(altUsersRes.data)) {
            agencyUsers = altUsersRes.data;
            fetchSuccess = true;
            console.log(`Found ${agencyUsers.length} users from alternative endpoint`);
          }
        } catch (altUsersErr) {
          console.warn("Failed to fetch agency users from alternative endpoint:", altUsersErr);
        }
      }
      
      // If still no success, try to get all users and filter by agency
      if (!fetchSuccess || agencyUsers.length === 0) {
        try {
          console.log("Trying to fetch all users and filter by agency");
          const allUsersRes = await axios.get('/api/users', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          });
          
          if (Array.isArray(allUsersRes.data)) {
            // Filter for users with matching agencyId and approved status
            agencyUsers = allUsersRes.data.filter(user => 
              user.agencyId === currentUser.agencyId && 
              (user.agencyStatus === 'approved' || user.role === 'agent_admin')
            );
            
            fetchSuccess = true;
            console.log(`Found ${agencyUsers.length} users by filtering all users`);
          }
        } catch (allUsersErr) {
          console.warn("Failed to fetch all users:", allUsersErr);
        }
      }
      
      // If still no success, try to use localStorage
      if (!fetchSuccess || agencyUsers.length === 0) {
        try {
          console.log("Trying to find agency users in localStorage");
          
          // Look for users in localStorage
          const localUsersJson = localStorage.getItem('users');
          if (localUsersJson) {
            const localUsers = JSON.parse(localUsersJson);
            
            if (Array.isArray(localUsers)) {
              agencyUsers = localUsers.filter(user => 
                user.agencyId === currentUser.agencyId && 
                (user.agencyStatus === 'approved' || user.role === 'agent_admin')
              );
              
              console.log(`Found ${agencyUsers.length} users in localStorage`);
            }
          }
          
          // Also check individual user records
          const allStorageKeys = Object.keys(localStorage);
          const userKeys = allStorageKeys.filter(key => key.startsWith('user_'));
          
          for (const key of userKeys) {
            try {
              const userData = JSON.parse(localStorage.getItem(key));
              if (userData && 
                  userData.agencyId === currentUser.agencyId && 
                  (userData.agencyStatus === 'approved' || userData.role === 'agent_admin') &&
                  !agencyUsers.some(u => u._id === userData._id)) {
                agencyUsers.push(userData);
              }
            } catch (e) {
              console.warn(`Error parsing user data from ${key}:`, e);
            }
          }
          
          // Make sure the current user is included (if they're part of this agency)
          const currentUserData = JSON.parse(localStorage.getItem('user'));
          if (currentUserData && 
              currentUserData.agencyId === currentUser.agencyId && 
              !agencyUsers.some(u => u._id === currentUserData._id)) {
            agencyUsers.push(currentUserData);
          }
          
          console.log(`Final count of agency users from localStorage: ${agencyUsers.length}`);
        } catch (localErr) {
          console.error("Error retrieving users from localStorage:", localErr);
        }
      }
      
      // Set the users in state
      setUsers(agencyUsers);

      // Fetch agency membership requests
      try {
        let requestsData = [];
        try {
          console.log("Fetching agency membership requests from API...");
          const requestsRes = await getAgencyRequests(currentUser.agencyId);
          
          if (Array.isArray(requestsRes)) {
            console.log(`Found ${requestsRes.length} agency requests from API`);
            requestsData = requestsRes;
            
            // Log the structure of the first request to help debug
            if (requestsData.length > 0) {
              console.log("First agency request structure:", safeLogObject(requestsData[0]));
            }
          } else {
            console.warn("Invalid agency requests data format:", requestsRes);
          }
        } catch (apiErr) {
          console.warn("Failed to fetch agency requests from API:", apiErr);
        }
        
        // If the API call failed or returned no results, check localStorage as fallback
        if (requestsData.length === 0) {
          console.log("Checking localStorage for agency membership requests");
          try {
            const localRequestsJson = localStorage.getItem('agencyRequests');
            if (localRequestsJson) {
              const localRequests = JSON.parse(localRequestsJson);
              
              // Filter for requests related to this agency and with pending status
              const relevantRequests = localRequests.filter(req => 
                req.agencyId === currentUser.agencyId && 
                req.status === 'pending'
              );
              
              if (relevantRequests.length > 0) {
                console.log(`Found ${relevantRequests.length} agency requests in localStorage`);
                
                // Log the structure of the first request from localStorage
                if (relevantRequests.length > 0) {
                  console.log("First localStorage agency request structure:", safeLogObject(relevantRequests[0]));
                }
                
                requestsData = relevantRequests;
              }
            }
          } catch (localErr) {
            console.error("Error reading agency requests from localStorage:", localErr);
          }
        }
        
        setAgencyRequests(requestsData);
      } catch (requestsErr) {
        console.warn("Failed to fetch agency requests:", requestsErr);
        setAgencyRequests([]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching agent dashboard data:", err);
      setError("Failed to load dashboard data. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    // Redirect if not agent admin
    if (!isAgentAdmin()) {
      navigate("/dashboard");
      return;
    }

    // Load data
    fetchData();
  }, [navigate, isAgentAdmin, currentUser]);

  // Handle tab change
  const handleTabChange = (tab) => {
    // Set the active tab and update refresh timestamp
    setActiveTab(tab);
    updateTabRefreshTime(tab);
    
    // Hide mobile tabs menu after selection
    setTabsVisible(false);
    
    // Refresh the data for the selected tab
    handleRefresh(tab);
  };

  // Toggle tabs visibility for mobile
  const toggleTabs = () => {
    setTabsVisible(!tabsVisible);
  };

  // Update agency users list after a membership approval
  const updateAgencyUsersList = async (userId, isApproved) => {
    if (!isApproved) return; // Only need to update users for approvals
    
    try {
      // First try the API to get updated user list
      try {
        const usersRes = await axios.get(`/api/agencies/${currentUser.agencyId}/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (Array.isArray(usersRes.data)) {
          setUsers(usersRes.data);
          console.log("Updated agency users list from API");
          return;
        }
      } catch (apiError) {
        console.warn("Failed to update agency users from API:", apiError);
      }
      
      // If API call fails, try to update from localStorage
      try {
        // Get the user details from localStorage
        const userJson = localStorage.getItem(`user_${userId}`);
        let userData;
        
        if (userJson) {
          userData = JSON.parse(userJson);
        } else {
          // If not found by ID, search in agencyRequests
          const requestsJson = localStorage.getItem('agencyRequests');
          if (requestsJson) {
            const requests = JSON.parse(requestsJson);
            const userRequest = requests.find(req => req.userId === userId || req._id === userId);
            
            if (userRequest) {
              userData = {
                _id: userRequest.userId || userId,
                name: userRequest.name || userRequest.user?.name || 'Unknown User',
                email: userRequest.email || userRequest.user?.email || 'No Email',
                phone: userRequest.phone || userRequest.user?.phone || 'N/A',
                role: 'user',
                createdAt: userRequest.createdAt || new Date().toISOString(),
                agencyId: currentUser.agencyId,
                agencyStatus: isApproved ? 'approved' : 'rejected'
              };
              
              // Save this user data for future reference
              localStorage.setItem(`user_${userData._id}`, JSON.stringify(userData));
            }
          }
        }
        
        // Also check main user storage
        if (!userData) {
          const mainUserJson = localStorage.getItem('user');
          if (mainUserJson) {
            const mainUser = JSON.parse(mainUserJson);
            if (mainUser._id === userId) {
              userData = {
                _id: mainUser._id,
                name: mainUser.name || 'Unknown User',
                email: mainUser.email || 'No Email',
                phone: mainUser.phone || 'N/A',
                role: mainUser.role || 'user',
                createdAt: mainUser.createdAt || new Date().toISOString(),
                agencyId: currentUser.agencyId,
                agencyStatus: 'approved'
              };
            }
          }
        }
        
        if (userData) {
          // Add this user to the users list if not already present
          const userExists = users.some(u => u._id === userData._id);
          
          if (!userExists) {
            const updatedUsers = [...users, userData];
            setUsers(updatedUsers);
            console.log("Added approved user to agency users list");
            
            // Also update the main users list in localStorage
            try {
              const localUsersJson = localStorage.getItem('users');
              if (localUsersJson) {
                const localUsers = JSON.parse(localUsersJson);
                
                // Add user if not already in the list
                if (!localUsers.some(u => u._id === userData._id)) {
                  localUsers.push(userData);
                  localStorage.setItem('users', JSON.stringify(localUsers));
                  console.log("Updated users list in localStorage");
                }
              } else {
                // Create new users list
                localStorage.setItem('users', JSON.stringify([userData]));
              }
            } catch (e) {
              console.warn("Error updating users list in localStorage:", e);
            }
          }
        }
      } catch (localErr) {
        console.error("Error updating users from localStorage:", localErr);
      }
    } catch (error) {
      console.error("Error updating agency users list:", error);
    }
  };

  // Handle agency request approval/rejection
  const handleAgencyRequest = async (requestId, isApproved) => {
    try {
      setError("");
      setSuccessMessage("");
      
      let userId = null;
      let requestData = null;

      // Check if this is a local request (has 'local-' prefix)
      const isLocalRequest = requestId.toString().startsWith('local-');
      
      if (isLocalRequest) {
        // Handle locally stored request
        const localRequestsJson = localStorage.getItem('agencyRequests');
        if (localRequestsJson) {
          const localRequests = JSON.parse(localRequestsJson);
          
          // Find the request
          const requestToUpdate = localRequests.find(req => req._id === requestId);
          if (!requestToUpdate) {
            throw new Error("Request not found");
          }
          
          // Save the userId and request data for later use
          userId = requestToUpdate.userId;
          requestData = requestToUpdate;
          
          // Find the user in localStorage and update their status
          const userKey = 'user';
          const allUsers = ['users', 'userCache', 'recentUsers'];
          
          // Update the user's agencyStatus in their own localStorage entry
          try {
            // First, try to update the user's own localStorage data
            let userUpdated = false;
            
            // Try in the main localStorage user entry
            try {
              const userJson = localStorage.getItem(userKey);
              if (userJson) {
                const userData = JSON.parse(userJson);
                if (userData._id === userId) {
                  userData.agencyId = currentUser.agencyId;
                  userData.agencyStatus = isApproved ? 'approved' : 'rejected';
                  userData.agencyStatusDate = new Date().toISOString();
                  userData.agencyName = currentUser.agencyName || agency?.name;
                  userData.hasPendingAgencyRequest = isApproved ? false : true;
                  localStorage.setItem(userKey, JSON.stringify(userData));
                  console.log(`Updated user ${userId} agency status to ${isApproved ? 'approved' : 'rejected'}`);
                  userUpdated = true;
                }
              }
            } catch (e) {
              console.error("Error updating main user entry:", e);
            }
            
            // Try in other user storage locations if not found in main user
            if (!userUpdated) {
              allUsers.forEach(storageKey => {
                try {
                  const dataJson = localStorage.getItem(storageKey);
                  if (!dataJson) return;
                  
                  const data = JSON.parse(dataJson);
                  
                  // If it's an array, find the user
                  if (Array.isArray(data)) {
                    const updatedData = data.map(user => {
                      if (user && user._id === userId) {
                        return {
                          ...user,
                          agencyId: currentUser.agencyId,
                          agencyName: currentUser.agencyName || agency?.name,
                          agencyStatus: isApproved ? 'approved' : 'rejected',
                          agencyStatusDate: new Date().toISOString(),
                          hasPendingAgencyRequest: isApproved ? false : true
                        };
                      }
                      return user;
                    });
                    localStorage.setItem(storageKey, JSON.stringify(updatedData));
                    userUpdated = true;
                  }
                  // If it's an object with user IDs as keys
                  else if (typeof data === 'object' && data !== null) {
                    if (data[userId]) {
                      data[userId].agencyId = currentUser.agencyId;
                      data[userId].agencyName = currentUser.agencyName || agency?.name;
                      data[userId].agencyStatus = isApproved ? 'approved' : 'rejected';
                      data[userId].agencyStatusDate = new Date().toISOString();
                      data[userId].hasPendingAgencyRequest = isApproved ? false : true;
                      localStorage.setItem(storageKey, JSON.stringify(data));
                      userUpdated = true;
                    }
                  }
                } catch (err) {
                  console.warn(`Error updating user in ${storageKey}:`, err);
                }
              });
            }
            
            // Store individual user data by ID for easier access
            try {
              localStorage.setItem(`user_${userId}`, JSON.stringify({
                _id: userId,
                agencyId: currentUser.agencyId,
                agencyName: currentUser.agencyName || agency?.name,
                agencyStatus: isApproved ? 'approved' : 'rejected',
                agencyStatusDate: new Date().toISOString(),
                hasPendingAgencyRequest: isApproved ? false : true,
                name: requestToUpdate.name || 'Unknown User',
                email: requestToUpdate.email || 'No Email',
                role: 'user'
              }));
            } catch (storageErr) {
              console.warn("Error storing individual user data:", storageErr);
            }
            
            console.log(`User status update ${userUpdated ? 'successful' : 'failed'}`);
          } catch (userUpdateErr) {
            console.error("Failed to update user agency status:", userUpdateErr);
          }
          
          // Update the request
          const updatedRequests = localRequests.map(req => {
            if (req._id === requestId) {
              return { 
                ...req, 
                status: isApproved ? 'approved' : 'rejected',
                statusDate: new Date().toISOString(),
                statusBy: currentUser?._id || 'agent-admin'
              };
            }
            return req;
          });
          
          // Save updated requests back to localStorage
          localStorage.setItem('agencyRequests', JSON.stringify(updatedRequests));
          
          // Update state to remove the processed request
          const updatedAgencyRequests = agencyRequests.filter(req => req._id !== requestId);
          setAgencyRequests(updatedAgencyRequests);
        }
      }
      
      // Attempt to update via API (regardless of local storage success)
      try {
        // Prepare the userId and status for the API
        const apiUserId = userId || (requestData ? requestData.userId : null) || requestId;
        
        if (apiUserId) {
          // Try the correct API endpoint format: PUT to /api/users/{userId}/agency-request/{action}
          try {
            const action = isApproved ? 'approve' : 'reject';
            const response = await axios.put(
              `/api/users/${apiUserId}/agency-request/${action}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            console.log(`API response for agency request ${action}:`, response.data);
          } catch (mainEndpointError) {
            console.warn("Main API endpoint failed:", mainEndpointError);
            
            // Try fallback endpoint formats
            try {
              // Try first fallback format: PUT to /api/agencies/{agencyId}/users/{userId}
              const response = await axios.put(
                `/api/agencies/${currentUser.agencyId}/users/${apiUserId}`,
                { 
                  approved: isApproved,
                  status: isApproved ? 'approved' : 'rejected'
                },
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              console.log(`API response for agency request ${isApproved ? 'approval' : 'rejection'} (fallback 1):`, response.data);
            } catch (format1Error) {
              console.warn("Fallback 1 API call failed:", format1Error);
              
              try {
                // Try second fallback format: POST to /api/agencies/{agencyId}/requests
                const response = await axios.post(
                  `/api/agencies/${currentUser.agencyId}/requests`,
                  { 
                    userId: apiUserId,
                    approved: isApproved,
                    requestId: requestId.toString().replace('local-', ''),
                    status: isApproved ? 'approved' : 'rejected'
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                      'Content-Type': 'application/json'
                    }
                  }
                );
                
                console.log(`API response for agency request ${isApproved ? 'approval' : 'rejection'} (fallback 2):`, response.data);
              } catch (format2Error) {
                console.warn("Fallback 2 API call failed:", format2Error);
                
                try {
                  // Try third fallback format: POST to /api/agency-requests/{requestId}
                  const response = await axios.post(
                    `/api/agency-requests/${requestId.toString().replace('local-', '')}`,
                    { 
                      approved: isApproved,
                      userId: apiUserId,
                      agencyId: currentUser.agencyId
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    }
                  );
                  
                  console.log(`API response for agency request ${isApproved ? 'approval' : 'rejection'} (fallback 3):`, response.data);
                } catch (format3Error) {
                  console.warn("All API formats failed:", format3Error);
                  throw format3Error;
                }
              }
            }
          }
          
          if (!isLocalRequest) {
            // Update the agency requests list
            const updatedRequests = agencyRequests.filter(req => 
              !(req._id === requestId || (req.userId === apiUserId && req._id.toString().includes(apiUserId)))
            );
            setAgencyRequests(updatedRequests);
          }
        } else {
          // Fall back to the original API path if we don't have a userId
          try {
            await axios.post(
              `/api/agencies/${currentUser.agencyId}/requests/${requestId}`,
              { approved: isApproved },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            if (!isLocalRequest) {
              // Update the agency requests list
              const updatedRequests = agencyRequests.filter(req => req._id !== requestId);
              setAgencyRequests(updatedRequests);
            }
          } catch (originalError) {
            console.warn("Original API path failed:", originalError);
            throw originalError;
          }
        }
      } catch (apiError) {
        console.warn(`API call for agency request ${isApproved ? 'approval' : 'rejection'} failed:`, apiError);
        // Since we've already updated localStorage and UI state, we can continue
      }
      
      // Update the agency users list if approved
      if (userId) {
        await updateAgencyUsersList(userId, isApproved);
      }
      
      setSuccessMessage(`Request ${isApproved ? 'approved' : 'rejected'} successfully`);
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh data after processing
      fetchData();
      
    } catch (err) {
      console.error(`Error ${isApproved ? 'approving' : 'rejecting'} agency request:`, err);
      setError(err.response?.data?.message || `Failed to ${isApproved ? 'approve' : 'reject'} request`);
    }
  };

  // Handle booking request approval/rejection
  const handleBookingRequest = async (bookingId, isApproved) => {
    try {
      setError("");
      setSuccessMessage("");
      
      console.log(`Processing booking request ${bookingId} - approved: ${isApproved}`);
      
      // Call the API service function with the booking ID and approval boolean
      await respondToBookingRequest(bookingId, isApproved);
      
      // Update the bookings list
      const updatedBookings = bookingRequests.filter(booking => booking._id !== bookingId);
      setBookingRequests(updatedBookings);
      
      setSuccessMessage(`Booking request ${isApproved ? 'approved' : 'rejected'} successfully`);
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh bookings data to make sure we have the latest
      fetchData();
    } catch (err) {
      console.error(`Error ${isApproved ? 'approving' : 'rejecting'} booking request:`, err);
      setError(err.response?.data?.message || `Failed to ${isApproved ? 'approve' : 'reject'} booking request`);
    }
  };

  // Function to handle deleting a user from the agency
  const handleDeleteUser = (userId) => {
    console.log(`Request to remove user ${userId} from agency`);
    // This is a placeholder - in a real implementation, 
    // this would make an API call to remove the user from the agency
    setSuccessMessage("User removal functionality not yet implemented");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const fetchBookingRequestDetails = async (requestId) => {
    try {
      setLoadingDetails(true);
      setDetailsError("");
      
      console.log(`Fetching booking request details for ID: ${requestId}`);
      
      // Make the API call to get booking request details
      const requestDetails = await getBookingRequestById(requestId);
      
      // Log full booking details for debugging
      console.log("Raw booking request details received:", requestDetails);
      
      // Check ALL possible document field combinations
      const possibleDocumentFields = [
        'documentUrl', 'document', 'attachmentUrl', 'fileUrl', 'file', 
        'upload', 'uploadUrl', 'attachment', 'receipt', 'receiptUrl'
      ];

      let foundDocumentUrl = null;
      let foundDocumentName = null;

      // First check if documentUrl exists
      if (requestDetails.documentUrl) {
        console.log("Primary document URL found:", requestDetails.documentUrl);
        foundDocumentUrl = requestDetails.documentUrl;
        foundDocumentName = requestDetails.documentOriginalName || "Document";
      } else {
        // Check all other possible document fields
        for (const field of possibleDocumentFields) {
          if (requestDetails[field] && typeof requestDetails[field] === 'string' && requestDetails[field].trim() !== '') {
            console.log(`Found document in alternative field '${field}':`, requestDetails[field]);
            foundDocumentUrl = requestDetails[field];
            
            // Try to find a corresponding name field
            const nameField = `${field.replace('Url', '')}Name`;
            const originalNameField = `${field.replace('Url', '')}OriginalName`;
            
            if (requestDetails[nameField]) {
              foundDocumentName = requestDetails[nameField];
            } else if (requestDetails[originalNameField]) {
              foundDocumentName = requestDetails[originalNameField];
            } else {
              foundDocumentName = "Document";
            }
            
            break;
          }
        }
      }
      
      // If we found a document URL, update the booking details
      if (foundDocumentUrl) {
        console.log("Found document URL:", foundDocumentUrl);
        requestDetails.documentUrl = foundDocumentUrl;
        requestDetails.documentOriginalName = foundDocumentName;
        
        // Process the URL to make sure it's absolute
        requestDetails.documentUrl = getAbsoluteDocumentUrl(requestDetails.documentUrl);
        console.log("Processed document URL:", requestDetails.documentUrl);
      } else {
        console.log("No document URL found in this booking request");
      }
      
      setSelectedBooking(requestDetails);
    } catch (error) {
      console.error("Error fetching booking request details:", error);
      setDetailsError("Failed to load booking request details. Please try again.");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Utility functions for booking display
  const getPropertyName = (booking) => {
    if (booking.lot) {
      return `${booking.lot.type || ''} ${booking.lot.lotNumber || ''}`;
    } else if (booking.unit) {
      return `${booking.unit.type || ''} ${booking.unit.lotNumber || ''}`;
    } else if (booking.specialLotId) {
      return `Type ${booking.specialLotType || 'Unknown'} ${booking.specialLotNumber || ''}`;
    }
    return 'Unknown Property';
  };

  const getClientName = (booking) => {
    if (booking.user && booking.user.name) {
      return booking.user.name;
    } else if (booking.creatorName) {
      return booking.creatorName;
    }
    return 'Unknown Client';
  };

  const getBookingDetails = (booking) => {
    let details = [];
    
    if (booking.creatorEmail) {
      details.push(booking.creatorEmail);
    } else if (booking.user && booking.user.email) {
      details.push(booking.user.email);
    }
    
    if (booking.creatorPhone) {
      details.push(booking.creatorPhone);
    } else if (booking.user && booking.user.phone) {
      details.push(booking.user.phone);
    }
    
    return details.join(' • ');
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'completed':
        return 'status-completed';
      case 'pending_admin':
        return 'status-pending-admin';
      case 'pending_agent':
        return 'status-pending';
      case 'checking':
        return 'status-checking';
      case 'rejected_by_agent':
      case 'rejected':
        return 'status-rejected';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };

  // Format status for display
  const formatStatus = (status) => {
    switch (status) {
      case 'pending_agent':
        return 'Pending Review';
      case 'pending_admin':
        return 'Pending Admin';
      case 'approved':
        return 'Approved';
      case 'rejected_by_agent':
      case 'rejected':
        return 'Rejected';
      case 'checking':
        return 'Checking';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    }
  };

  // Render the dashboard overview
  const renderOverview = () => {
    return (
      <div className="dashboard-overview">
        {agency && (
          <div className="agency-info-card">
            <h4>{agency.name}</h4>
            <p><strong>Email:</strong> {agency.contactEmail}</p>
            <p><strong>Phone:</strong> {agency.contactPhone || 'N/A'}</p>
            <p><strong>Address:</strong> {agency.address || 'N/A'}</p>
            <div className="agency-icon">
              <i className="fas fa-building"></i>
            </div>
          </div>
        )}
        
        <div className="stats-grid">
          <div className="stat-card">
            <h4>Agency Members</h4>
            <p className="stat-number">{users.length}</p>
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
          </div>
          <div className="stat-card">
            <h4>Pending Bookings</h4>
            <p className="stat-number">{bookingRequests.length}</p>
            <div className="stat-icon">
              <i className="fas fa-clipboard-list"></i>
            </div>
          </div>
          <div className="stat-card">
            <h4>Membership Requests</h4>
            <p className="stat-number">{agencyRequests.length}</p>
            <div className="stat-icon">
              <i className="fas fa-user-plus"></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render agency users
  const renderAgencyUsers = () => {
    return (
      <div className="agent-section">
        <div className="section-header">
          <h3>Agency Users</h3>
          
          <div className="section-controls">
            {tabLastRefreshTimes.users && (
              <div className="last-refresh">
                Last refreshed: {tabLastRefreshTimes.users}
              </div>
            )}
            
            <button 
              className="refresh-btn"
              onClick={() => handleRefresh('users')}
              disabled={loading}
            >
              Refresh Users
            </button>
          </div>
        </div>
        
        {users.length === 0 ? (
          <div className="no-data-message">
            <p>No users found for your agency.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id}>
                    <td>{user.name || 'N/A'}</td>
                    <td>{user.email || 'N/A'}</td>
                    <td>{user.phone || 'N/A'}</td>
                    <td>
                      <span className={`role-badge ${user.role === 'agent_admin' ? 'role-agent' : 'role-user'}`}>
                        {user.role === 'agent_admin' ? 'Agent Admin' : 'User'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.agencyStatus === 'approved' ? 'status-approved' : 'status-pending'}`}>
                        {user.agencyStatus === 'approved' ? 'Approved' : 
                         user.agencyStatus === 'pending' ? 'Pending' : 
                         user.agencyStatus || 'Unknown'}
                      </span>
                    </td>
                    <td>
                      <div className="edit-buttons">
                        {user._id !== currentUser._id && (
                          <>
                            <button
                              className="btn-danger btn-sm"
                              onClick={() => handleDeleteUser(user._id)}
                              title="Remove user from agency"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render booking requests
  const renderBookingRequests = () => {
    // Handle the response to a booking request
    const handleRequestResponse = async (requestId, isApproved) => {
      try {
        setLoading(true);
        
        // Prepare request data
        const status = isApproved ? 'approved' : 'rejected_by_agent';
        const message = isApproved 
          ? "Booking request approved by agent" 
          : "Booking request rejected by agent";
        
        console.log(`Processing booking request ${requestId}: ${status}`);
        
        // Use the correct API endpoint and the respondToBookingRequest function
        try {
          const response = await respondToBookingRequest(requestId, status, message);
          console.log('Booking response successful:', response);
          
          // Update the local state immediately
          const updatedRequests = bookingRequests.map(req => 
            req._id === requestId ? { ...req, status, agentNotes: message } : req
          );
          
          setBookingRequests(updatedRequests);
          setSuccessMessage(`Booking request ${isApproved ? 'approved' : 'rejected'} successfully`);
          
          // Refresh the data immediately to get updated lot statuses
          console.log('Refreshing booking requests and lots data...');
          await handleRefresh('bookings');
          
          // Also trigger a refresh of the overview to update lot counts
          setTimeout(() => {
            handleRefresh('overview');
          }, 1000);
          
        } catch (apiError) {
          console.error('API call failed, trying direct axios call:', apiError);
          
          // Fallback to direct axios call
          const response = await axios.put(
            `${window.location.origin}/api/bookings/${requestId}/status-update`,
            { status, message },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Direct axios call successful:', response.data);
          
          // Update the local state
          const updatedRequests = bookingRequests.map(req => 
            req._id === requestId ? { ...req, status, agentNotes: message } : req
          );
          
          setBookingRequests(updatedRequests);
          setSuccessMessage(`Booking request ${isApproved ? 'approved' : 'rejected'} successfully`);
          
          // Refresh the data
          await handleRefresh('bookings');
          setTimeout(() => {
            handleRefresh('overview');
          }, 1000);
        }
        
      } catch (error) {
        console.error("Error responding to booking request:", error);
        setError(`Failed to ${isApproved ? 'approve' : 'reject'} booking request: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    // Format date in a readable format
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // Get property type and number with fallbacks
    const getPropertyInfo = (request) => {
      // Check for nested lot object
      if (request.lot) {
        return {
          type: request.lot.type || 'Lot',
          number: request.lot.lotNumber || request.lot.number || 'N/A'
        };
      }
      
      // Check for nested unit object
      if (request.unit) {
        return {
          type: request.unit.type || 'Unit',
          number: request.unit.unitNumber || request.unit.number || 'N/A'
        };
      }
      
      // Check for direct properties on the request
      return {
        type: request.lotType || request.propertyType || request.type || 'Property',
        number: request.lotNumber || request.propertyNumber || request.number || 'N/A'
      };
    };
    
    // Get client information with fallbacks
    const getClientInfo = (request) => {
      // Check for nested user object
      if (request.user) {
        return {
          name: request.user.name || 'Unknown Client',
          email: request.user.email || 'N/A',
          phone: request.user.phone || request.contactPhone || 'N/A'
        };
      }
      
      // Use direct properties with fallbacks
      return {
        name: request.name || request.clientName || request.userName || 'Unknown Client',
        email: request.email || request.clientEmail || request.userEmail || 'N/A',
        phone: request.phone || request.contactPhone || request.clientPhone || 'N/A'
      };
    };
    
    // Add console logging for debugging booking requests
    console.log("Booking Requests Data:", bookingRequests);
    
    // Log document fields specifically
    if (bookingRequests.length > 0) {
      console.log("Example of first booking request:", bookingRequests[0]);
      
      // Check for document-related fields in the first booking request
      const docFields = {};
      const firstRequest = bookingRequests[0];
      for (const key in firstRequest) {
        if (key.toLowerCase().includes('doc') || 
            key.toLowerCase().includes('file') || 
            key.toLowerCase().includes('attachment') ||
            key.toLowerCase().includes('upload')) {
          docFields[key] = firstRequest[key];
        }
      }
      console.log("Document-related fields in first booking request:", docFields);
    }
    
    return (
      <div className="agent-section">
        <div className="section-header">
          <h3>Booking Requests</h3>
          
          <div className="section-controls">
            {tabLastRefreshTimes.bookings && (
              <div className="last-refresh">
                Last refreshed: {tabLastRefreshTimes.bookings}
              </div>
            )}
            
            <button 
              className="refresh-btn"
              onClick={() => handleRefresh('bookings')}
              disabled={loading}
            >
              Refresh Bookings
            </button>
          </div>
        </div>
        
        {bookingRequests.length === 0 ? (
          <div className="no-data-message">
            <p>No pending booking requests.</p>
          </div>
        ) : (
          <div className="booking-requests-container">
            <table className="booking-requests-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Client</th>
                  <th>Contact</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookingRequests.map(request => {
                  // Get property and client info with fallbacks
                  const propertyInfo = getPropertyInfo(request);
                  const clientInfo = getClientInfo(request);
                  
                  return (
                    <tr key={request._id} className={`request-row status-${request.status}`}>
                      <td className="property-info">
                        <div className="property-type">{propertyInfo.type}</div>
                        <div className="property-number">{propertyInfo.number}</div>
                      </td>
                      <td className="client-info">
                        <div className="client-name">{clientInfo.name}</div>
                        <div className="client-email">{clientInfo.email}</div>
                      </td>
                      <td className="contact-info">
                        <div className="phone">{clientInfo.phone || 'N/A'}</div>
                      </td>
                      <td className="date-info">
                        {formatDate(request.createdAt)}
                      </td>
                      <td className="status-info">
                        <span className={`status-badge status-${request.status}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1).replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="actions-info">
                        <button 
                          className="btn-view-details"
                          onClick={() => {
                            setSelectedBookingForModal(request);
                            setShowBookingModal(true);
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Booking Details Modal */}
            {selectedBooking && (
              <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Booking Request Details</h3>
                    <button className="close-btn" onClick={() => setSelectedBooking(null)}>×</button>
                  </div>
                  
                  {loadingDetails ? (
                    <div className="modal-loading">
                      <p>Loading request details...</p>
                    </div>
                  ) : detailsError ? (
                    <div className="modal-error">
                      <p>{detailsError}</p>
                      <button 
                        className="btn-retry"
                        onClick={() => fetchBookingRequestDetails(selectedBooking._id)}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="modal-body">
                      {/* Debug document info */}
                      {console.log("Modal document debug:", {
                        hasDocumentUrl: !!selectedBooking.documentUrl,
                        documentUrl: selectedBooking.documentUrl,
                        documentName: selectedBooking.documentOriginalName,
                        bookingId: selectedBooking._id,
                        allKeys: Object.keys(selectedBooking)
                      })}
                      <div className="detail-row">
                        <span className="detail-label">Property:</span>
                        <span className="detail-value">{selectedBooking.lotType} - {selectedBooking.lotNumber}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Client Name:</span>
                        <span className="detail-value">{selectedBooking.name}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{selectedBooking.email}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Phone:</span>
                        <span className="detail-value">{selectedBooking.phone}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Date Submitted:</span>
                        <span className="detail-value">{formatDate(selectedBooking.createdAt)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span className={`status-badge status-${selectedBooking.status}`}>
                          {selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Description:</span>
                        <div className="detail-text">{selectedBooking.description || 'No description provided'}</div>
                      </div>
                      
                      {/* Direct document section - simplified approach */}
                      {selectedBooking.documentUrl && (
                        <div className="detail-row">
                          <span className="detail-label">Document:</span>
                          <div className="detail-value document-links">
                            <a 
                              href={getAbsoluteDocumentUrl(selectedBooking.documentUrl)} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="document-link"
                            >
                              {selectedBooking.documentOriginalName || "View Uploaded Document"}
                            </a>
                            <button 
                              className="btn-document-preview"
                              onClick={() => window.open(getAbsoluteDocumentUrl(selectedBooking.documentUrl), '_blank')}
                            >
                              Open Document
                            </button>
                            
                            {/* Show URL for debugging */}
                            <div className="document-url-debug">
                              Document URL: {selectedBooking.documentUrl}
                            </div>
                            
                            {/* Document preview for images */}
                            {(selectedBooking.documentUrl.toLowerCase().endsWith('.jpg') || 
                              selectedBooking.documentUrl.toLowerCase().endsWith('.jpeg') || 
                              selectedBooking.documentUrl.toLowerCase().endsWith('.png')) && (
                              <div className="document-preview-container">
                                <p>Document Preview:</p>
                                <img 
                                  src={getAbsoluteDocumentUrl(selectedBooking.documentUrl)} 
                                  alt="Document Preview" 
                                  className="document-preview-image" 
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {selectedBooking.status !== 'pending' && (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">Response Date:</span>
                            <span className="detail-value">{formatDate(selectedBooking.agentResponseDate)}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">Agent Notes:</span>
                            <div className="detail-text">{selectedBooking.agentNotes || 'No notes provided'}</div>
                          </div>
                          {selectedBooking.respondedBy && (
                            <div className="detail-row">
                              <span className="detail-label">Responded By:</span>
                              <span className="detail-value">
                                {selectedBooking.respondedBy.name || selectedBooking.respondedBy.email || 'Unknown User'}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  <div className="modal-footer">
                    {selectedBooking.status === 'pending' && !loadingDetails && !detailsError ? (
                      <>
                        <button
                          className="btn-approve" 
                          onClick={() => {
                            handleRequestResponse(selectedBooking._id, true);
                            setSelectedBooking(null);
                          }}
                          disabled={loading}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn-reject" 
                          onClick={() => {
                            handleRequestResponse(selectedBooking._id, false);
                            setSelectedBooking(null);
                          }}
                          disabled={loading}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <button 
                        className="btn-close" 
                        onClick={() => setSelectedBooking(null)}
                      >
                        Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* New Enhanced Booking Modal */}
            <AgentBookingModal
              booking={selectedBookingForModal}
              isOpen={showBookingModal}
              onClose={() => {
                setShowBookingModal(false);
                setSelectedBookingForModal(null);
              }}
              onApprove={(bookingId) => handleRequestResponse(bookingId, true)}
              onReject={(bookingId) => handleRequestResponse(bookingId, false)}
              loading={loading}
            />
          </div>
        )}
      </div>
    );
  };

  // Render booking history tab
  const renderBookingHistory = () => {
    // Format the status for display
    const formatStatus = (status) => {
      switch (status) {
        case 'pending_agent':
          return 'Pending Agent';
        case 'pending_admin':
          return 'Pending Admin';
        case 'approved':
          return 'Approved';
        case 'rejected_by_agent':
          return 'Rejected';
        case 'checking':
          return 'Checking';
        case 'completed':
          return 'Completed';
        case 'cancelled':
          return 'Cancelled';
        default:
          return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
      }
    };
    
    // Get status badge class
    const getStatusBadgeClass = (status) => {
      switch (status) {
        case 'approved':
          return 'status-approved';
        case 'completed':
          return 'status-completed';
        case 'pending_admin':
          return 'status-pending-admin';
        case 'pending_agent':
          return 'status-pending';
        case 'checking':
          return 'status-checking';
        case 'rejected_by_agent':
        case 'rejected':
          return 'status-rejected';
        case 'cancelled':
          return 'status-cancelled';
        default:
          return 'status-unknown';
      }
    };
    
    return (
      <div className="admin-section">
        <h3>Booking History</h3>
        
        {bookingHistory.length === 0 ? (
          <p>No booking history found.</p>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Property</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookingHistory
                  .filter(booking => booking.status !== 'pending_agent') // Filter out pending_agent bookings
                  .map(booking => (
                  <tr key={booking._id}>
                    <td>{booking._id.substring(booking._id.length - 6)}</td>
                    <td>
                      {booking.lot ? (
                        `${booking.lot.type || ''} ${booking.lot.lotNumber || ''}`
                      ) : booking.unit ? (
                        `${booking.unit.type || ''} ${booking.unit.lotNumber || ''}`
                      ) : booking.specialLotId ? (
                        `Type ${booking.specialLotType} ${booking.specialLotNumber}`
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>{booking.user?.name || booking.creatorName || 'Unknown'}</td>
                    <td>{new Date(booking.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                        {formatStatus(booking.status)}
                      </span>
                    </td>
                    <td>
                      {/* Only display the request message/description as notes */}
                      {booking.requestMessage || booking.description || 'No description'}
                    </td>
                    <td>
                      <button 
                        className="btn-details"
                        onClick={() => setSelectedBooking(booking)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Booking Details Modal - shown when selectedBooking is set */}
        {selectedBooking && (
          <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Booking Details</h3>
                <button className="close-btn" onClick={() => setSelectedBooking(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <span className="detail-label">Booking ID:</span>
                  <span className="detail-value">{selectedBooking._id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Property:</span>
                  <span className="detail-value">
                    {selectedBooking.lot ? (
                      `${selectedBooking.lot.type || ''} ${selectedBooking.lot.lotNumber || ''}`
                    ) : selectedBooking.unit ? (
                      `${selectedBooking.unit.type || ''} ${selectedBooking.unit.lotNumber || ''}`
                    ) : selectedBooking.specialLotId ? (
                      `Type ${selectedBooking.specialLotType} ${selectedBooking.specialLotNumber}`
                    ) : (
                      'N/A'
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Client:</span>
                  <span className="detail-value">{selectedBooking.user?.name || selectedBooking.creatorName || 'Unknown'}</span>
                </div>
                {selectedBooking.user?.email && (
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedBooking.user.email}</span>
                  </div>
                )}
                {(selectedBooking.user?.phone || selectedBooking.creatorPhone) && (
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{selectedBooking.user?.phone || selectedBooking.creatorPhone}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Date Created:</span>
                  <span className="detail-value">{new Date(selectedBooking.createdAt).toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`status-badge ${getStatusBadgeClass(selectedBooking.status)}`}>
                    {formatStatus(selectedBooking.status)}
                  </span>
                </div>
                {(selectedBooking.requestMessage || selectedBooking.description) && (
                  <div className="detail-row">
                    <span className="detail-label">Request Message:</span>
                    <div className="detail-text">{selectedBooking.requestMessage || selectedBooking.description}</div>
                  </div>
                )}
                {selectedBooking.agentNotes && (
                  <div className="detail-row">
                    <span className="detail-label">Agent Notes:</span>
                    <div className="detail-text">{selectedBooking.agentNotes}</div>
                  </div>
                )}
                {selectedBooking.adminNotes && (
                  <div className="detail-row">
                    <span className="detail-label">Admin Notes:</span>
                    <div className="detail-text">{selectedBooking.adminNotes}</div>
                  </div>
                )}
                
                {/* Enhanced document section to the booking history modal with better visibility */}
                {selectedBooking.documentUrl && (
                  <div className="detail-row document-section">
                    <span className="detail-label">Document:</span>
                    <div className="detail-value document-links">
                      <a 
                        href={getAbsoluteDocumentUrl(selectedBooking.documentUrl)} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="document-link primary-document-link"
                      >
                        <i className="fas fa-file-download"></i>
                        {(() => {
                          // Try to use documentOriginalName if available
                          if (selectedBooking.documentOriginalName) {
                            return selectedBooking.documentOriginalName;
                          }
                          
                          // If not available, extract filename from URL
                          try {
                            const url = selectedBooking.documentUrl;
                            // Get the last part of the URL (the filename)
                            const urlParts = url.split('/');
                            const filename = urlParts[urlParts.length - 1];
                            return filename || "View Uploaded Document";
                          } catch (err) {
                            return "View Uploaded Document";
                          }
                        })()}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn-close" 
                  onClick={() => setSelectedBooking(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render agency membership requests
  const renderAgencyRequests = () => {
    // Debug log to inspect the structure of agency requests
    console.log("Agency Requests Data:", agencyRequests.map(req => ({
      id: req._id,
      userId: req.userId,
      hasUser: !!req.user,
      name: req.name || (req.user && req.user.name) || 'N/A',
      email: req.email || (req.user && req.user.email) || 'N/A',
      requestDate: req.createdAt || req.agencyRequestDate
    })));
    
    // Helper function to extract user data from any source
    const extractUserData = (request) => {
      // Check all possible places for user data
      const possibleSources = [
        // Direct fields on the request
        { name: request.name, email: request.email },
        // User object if populated
        request.user ? { name: request.user.name, email: request.user.email } : null,
        // User data object if present
        request.userData ? { name: request.userData.name, email: request.userData.email } : null,
        // User info object if present
        request.userInfo ? { name: request.userInfo.name, email: request.userInfo.email } : null
      ];
      
      // Find first source that has both name and email
      const completeSource = possibleSources.find(
        source => source && source.name && source.email
      );
      
      if (completeSource) {
        return completeSource;
      }
      
      // If no complete source, compile best name and email from any source
      let bestName = 'Unknown User';
      let bestEmail = 'No Email';
      
      possibleSources.forEach(source => {
        if (source) {
          if (source.name) bestName = source.name;
          if (source.email) bestEmail = source.email;
        }
      });
      
      // Try to look up the user in the users array if we have a userId
      if (request.userId && (bestName === 'Unknown User' || bestEmail === 'No Email')) {
        const foundUser = users.find(u => u._id === request.userId);
        if (foundUser) {
          if (bestName === 'Unknown User' && foundUser.name) bestName = foundUser.name;
          if (bestEmail === 'No Email' && foundUser.email) bestEmail = foundUser.email;
        }
      }
      
      // Try localStorage as last resort
      try {
        if (request.userId && (bestName === 'Unknown User' || bestEmail === 'No Email')) {
          const userJson = localStorage.getItem(`user_${request.userId}`);
          if (userJson) {
            const userData = JSON.parse(userJson);
            if (bestName === 'Unknown User' && userData.name) bestName = userData.name;
            if (bestEmail === 'No Email' && userData.email) bestEmail = userData.email;
          }
        }
      } catch (err) {
        console.warn(`Error retrieving user ${request.userId} from localStorage:`, err);
      }
      
      return { name: bestName, email: bestEmail };
    };
    
    // Add user data to requests that might be missing it
    const enhancedRequests = agencyRequests.map(request => {
      const userData = extractUserData(request);
      
      return {
        ...request,
        displayName: userData.name,
        displayEmail: userData.email
      };
    });
    
    return (
      <div className="admin-section">
        <h3>Agency Membership Requests</h3>
        
        {enhancedRequests.length === 0 ? (
          <p>No pending membership requests.</p>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Request Date</th>
                  <th>Message</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enhancedRequests.map(request => (
                  <tr key={request._id}>
                    <td>{request.displayName}</td>
                    <td>{request.displayEmail}</td>
                    <td>{new Date(request.createdAt || request.agencyRequestDate || Date.now()).toLocaleDateString()}</td>
                    <td>{request.message || request.agencyRequestMessage || 'No message'}</td>
                    <td>
                      <div className="edit-buttons">
                        <button 
                          className="btn-secondary btn-save"
                          onClick={() => handleAgencyRequest(request._id, true)}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn-secondary btn-delete"
                          onClick={() => handleAgencyRequest(request._id, false)}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render different tabs based on activeTab state
  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "users":
        return renderAgencyUsers();
      case "bookings":
        return renderBookingRequests();
      case "history":
        return renderBookingHistory();
      case "requests":
        return renderAgencyRequests();
      default:
        return renderOverview();
    }
  };

  // Property Booking Tab component
  const PropertyBookingTab = () => {
    // Local state for property booking form
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState("");
    const [propertyId, setPropertyId] = useState("");
    const [requestMessage, setRequestMessage] = useState("");
    const [contactPhone, setContactPhone] = useState("");
    const [documentUrl, setDocumentUrl] = useState("");
    const [bookingStatus, setBookingStatus] = useState({ loading: false, success: "", error: "" });
    
    // Fetch clients for this agency on component mount
    useEffect(() => {
      const fetchClients = async () => {
        try {
          // Only fetch when tab is active
          if (activeTab !== "property-booking") return;
          
          setBookingStatus({ ...bookingStatus, loading: true, error: "" });
          
          // Get all users for this agency
          const response = await axios.get(
            `${window.location.origin}/api/users/agency/${currentUser.agencyId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.data && Array.isArray(response.data)) {
            // Filter out non-regular users (keep only client users)
            const clientUsers = response.data.filter(user => 
              user.role === 'user' && user.agencyStatus === 'approved'
            );
            
            console.log(`Found ${clientUsers.length} clients for this agency`);
            setClients(clientUsers);
          } else {
            console.warn("Unexpected response format when fetching clients:", response.data);
            setClients([]);
          }
          
          setBookingStatus({ ...bookingStatus, loading: false });
        } catch (error) {
          console.error("Error fetching agency clients:", error);
          setBookingStatus({ 
            ...bookingStatus, 
            loading: false, 
            error: "Failed to load agency clients. Please try again."
          });
        }
      };
      
      fetchClients();
    }, [activeTab, currentUser.agencyId, bookingStatus]);
    
    // Handle booking form submission
    const handleBookingSubmit = async (e) => {
      e.preventDefault();
      
      // Validate form
      if (!propertyId.trim()) {
        setBookingStatus({ ...bookingStatus, error: "Property ID is required" });
        return;
      }
      
      if (!selectedClient) {
        setBookingStatus({ ...bookingStatus, error: "Please select a client" });
        return;
      }
      
      try {
        setBookingStatus({ loading: true, success: "", error: "" });
        
        // Determine if this is a compound ID
        const isCompoundId = propertyId.match(/^type-[a-z]-\d+$/i);
        
        // Prepare the booking data
        const bookingData = {
          propertyId: propertyId,
          userId: selectedClient,
          requestMessage: requestMessage,
          contactPhone: contactPhone,
          documentUrl: documentUrl
        };
        
        console.log("Creating booking for client with data:", bookingData);
        
        // Use the appropriate endpoint based on ID format
        let response;
        if (isCompoundId) {
          // Use the property endpoint for compound IDs
          response = await axios.post(
            `${window.location.origin}/api/bookings/property`,
            bookingData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
        } else {
          // Use the standard request endpoint for regular IDs
          // First, determine if this is a lot ID or unit ID
          const isLotId = propertyId.length === 24; // MongoDB ID is 24 chars
          
          // Add the appropriate ID field
          if (isLotId) {
            bookingData.lotId = propertyId;
          } else {
            bookingData.unitId = propertyId;
          }
          
          response = await axios.post(
            `${window.location.origin}/api/bookings/request`,
            bookingData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
        }
        
        console.log("Booking created successfully:", response.data);
        
        // Reset form and show success message
        setPropertyId("");
        setRequestMessage("");
        setContactPhone("");
        setDocumentUrl("");
        
        setBookingStatus({
          loading: false,
          success: "Booking created successfully for your client!",
          error: ""
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setBookingStatus(prev => ({ ...prev, success: "" }));
        }, 3000);
        
      } catch (error) {
        console.error("Error creating booking:", error);
        
        let errorMessage = "Failed to create booking. Please try again.";
        
        // Extract more specific error message if available
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        setBookingStatus({
          loading: false,
          success: "",
          error: errorMessage
        });
      }
    };
    
    return (
      <div className="agent-dashboard-tab">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Book Property for Client</h2>
          </div>
          <div className="card-body">
            <p className="mb-3">
              Use this form to create a booking request on behalf of one of your agency's clients.
            </p>
            
            {/* Success and Error Messages */}
            {bookingStatus.success && (
              <div className="alert alert-success">{bookingStatus.success}</div>
            )}
            
            {bookingStatus.error && (
              <div className="alert alert-danger">{bookingStatus.error}</div>
            )}
            
            {/* Booking Form */}
            <form onSubmit={handleBookingSubmit}>
              <div className="form-group mb-3">
                <label htmlFor="clientSelect">Select Client</label>
                <select 
                  id="clientSelect" 
                  className="form-control"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  required
                >
                  <option value="">-- Select a client --</option>
                  {clients.map(client => (
                    <option key={client._id} value={client._id}>
                      {client.name} ({client.email})
                    </option>
                  ))}
                </select>
                {clients.length === 0 && !bookingStatus.loading && (
                  <div className="text-muted mt-2">
                    No clients found. Your agency needs to have approved users to create bookings for them.
                  </div>
                )}
              </div>
              
              <div className="form-group mb-3">
                <label htmlFor="propertyId">Property ID</label>
                <input
                  type="text"
                  id="propertyId"
                  className="form-control"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  placeholder="Enter property ID (e.g., type-a-16252)"
                  required
                />
                <small className="form-text text-muted">
                  Enter a compound ID like "type-a-16252" or a standard MongoDB ID
                </small>
              </div>
              
              <div className="form-group mb-3">
                <label htmlFor="requestMessage">Message (Optional)</label>
                <textarea
                  id="requestMessage"
                  className="form-control"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Enter any additional details or special requests"
                  rows="3"
                />
              </div>
              
              <div className="form-group mb-3">
                <label htmlFor="contactPhone">Contact Phone (Optional)</label>
                <input
                  type="text"
                  id="contactPhone"
                  className="form-control"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Enter contact phone number"
                />
              </div>
              
              <div className="form-group mb-3">
                <label htmlFor="documentUrl">Document URL (Optional)</label>
                <input
                  type="text"
                  id="documentUrl"
                  className="form-control"
                  value={documentUrl}
                  onChange={(e) => setDocumentUrl(e.target.value)}
                  placeholder="Enter document URL if applicable"
                />
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={bookingStatus.loading}
              >
                {bookingStatus.loading ? "Processing..." : "Create Booking Request"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  };
  
  // Add a wrapper function to render the PropertyBookingTab component
  const renderPropertyBooking = () => {
    return <PropertyBookingTab />;
  };

  // Function to handle refresh for all tabs
  const handleRefresh = (tab = activeTab) => {
    try {
      console.log(`Refreshing tab: ${tab}`);
      setError("");
      setSuccessMessage("");
      
      updateTabRefreshTime(tab);
      
      switch (tab) {
        case "overview":
          fetchData();
          break;
        case "users":
          // Fetch updated users list
          if (currentUser?.agencyId) {
            axios.get(`/api/agencies/${currentUser.agencyId}/users`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })
              .then(res => {
                if (Array.isArray(res.data)) {
                  setUsers(res.data);
                  setSuccessMessage("Users list refreshed");
                }
              })
              .catch(err => {
                console.error("Error refreshing users:", err);
                setError("Couldn't refresh users");
              });
          }
          break;
        case "bookings":
          const refreshBookings = async () => {
            try {
              setLoading(true);
              // Use the getAgencyBookingRequests API function for consistency
              const bookingRequestsData = await getAgencyBookingRequests();
              setBookingRequests(bookingRequestsData);
              setSuccessMessage("Booking requests refreshed");
              setLoading(false);
            } catch (err) {
              console.error("Error refreshing booking requests:", err);
              setError("Couldn't refresh booking requests");
              setLoading(false);
            }
          };
          
          refreshBookings();
          break;
        case "history":
          const refreshHistory = async () => {
            try {
              setLoading(true);
              const historyRes = await getAgencyBookingHistory();
              if (Array.isArray(historyRes)) {
                // Filter to exclude user requests (pending_agent status)
                const filteredHistory = historyRes.filter(booking => booking.status !== 'pending_agent');
                setBookingHistory(filteredHistory);
                setSuccessMessage("Booking history refreshed");
              }
              setLoading(false);
            } catch (err) {
              console.error("Error refreshing booking history:", err);
              setError("Couldn't refresh booking history");
              setLoading(false);
            }
          };
          
          refreshHistory();
          break;
        case "requests":
          const refreshAgencyRequests = async () => {
            try {
              setLoading(true);
              await fetchAgencyRequests();
              setSuccessMessage("Agency membership requests refreshed");
              setLoading(false);
            } catch (err) {
              console.error("Error refreshing agency requests:", err);
              setError("Couldn't refresh agency requests");
              setLoading(false);
            }
          };
          
          refreshAgencyRequests();
          break;
        default:
          fetchData();
      }
    } catch (error) {
      console.error("Error during refresh:", error);
      setError("An error occurred while refreshing");
    }
  };

  if (loading) {
    return (
      <div className="agent-dashboard">
        <div className="admin-content-fullwidth">
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-dashboard">
        <div className="admin-content-fullwidth">
          <div className="error-container">
            <h2>Error</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={fetchData}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard">
      <div className="admin-content-fullwidth">
        <div className="admin-header">
          <div>
            <h1>Agent Dashboard</h1>
            <p className="welcome-message">
              Welcome back, {currentUser?.name || "Agent Admin"}
            </p>
          </div>
        </div>

        {/* Mobile tabs toggle button */}
        <button className="tabs-toggle" onClick={toggleTabs}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
          </svg>
          {activeTab === "overview" ? "Dashboard Menu" : activeTab.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
        </button>

        <div className={`admin-tabs ${tabsVisible ? 'show' : ''}`}>
          <button
            className={activeTab === "overview" ? "active" : ""}
            onClick={() => handleTabChange("overview")}
          >
            Overview
          </button>
          <button
            className={activeTab === "users" ? "active" : ""}
            onClick={() => handleTabChange("users")}
          >
            Agency Users
          </button>
          <button
            className={activeTab === "bookings" ? "active" : ""}
            onClick={() => handleTabChange("bookings")}
          >
            Booking Requests
          </button>
          <button
            className={activeTab === "history" ? "active" : ""}
            onClick={() => handleTabChange("history")}
          >
            Booking History
          </button>
          <button
            className={activeTab === "requests" ? "active" : ""}
            onClick={() => handleTabChange("requests")}
          >
            Agency Requests
          </button>
        </div>

        {successMessage && <div className="success-message">{successMessage}</div>}
        {error && <div className="error-message">{error}</div>}
        
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AgentDashboard;
